import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, ArrowUpRight, ArrowDownRight, Minus, GitCompare } from 'lucide-react';
import { cn } from '../../utils/cn';
import { Epic, Session } from "../../types";

interface CompareModalProps {
    isOpen: boolean;
    onClose: () => void;
    currentEpics: Epic[];
    sessions: Session[];
}

interface ComparisonResult {
    session: Session;
    changes: {
        id: string;
        title: string;
        past: Epic;
        current: Epic;
        scoreDiff: number;
        laneChanged: boolean;
    }[];
    added: Epic[];
    removed: Epic[];
    unchanged: Epic[];
}

export function CompareModal({ isOpen, onClose, currentEpics, sessions }: CompareModalProps) {
    const [selectedSessionId, setSelectedSessionId] = useState('');

    // Get comparison data
    const comparison = useMemo<ComparisonResult | null>(() => {
        if (!selectedSessionId || !sessions) return null;

        const session = sessions.find(s => s.id === selectedSessionId);
        if (!session) return null;

        const sessionMap = new Map(session.epics.map(e => [e.id, e]));
        const currentMap = new Map(currentEpics.map(e => [e.id, e]));

        const changes: ComparisonResult['changes'] = [];
        const added: Epic[] = [];
        const removed: Epic[] = [];
        const unchanged: Epic[] = [];

        // Find changes and removals
        session.epics.forEach(pastEpic => {
            const currentEpic = currentMap.get(pastEpic.id);
            if (!currentEpic) {
                removed.push(pastEpic);
            } else {
                // Check for significant differences (Score or Lane)
                const scoreDiff = (currentEpic.wsjf || 0) - (pastEpic.wsjf || 0);
                const laneChanged = currentEpic.lane !== pastEpic.lane;

                if (Math.abs(scoreDiff) > 0.1 || laneChanged) {
                    changes.push({
                        id: pastEpic.id,
                        title: pastEpic.title,
                        past: pastEpic,
                        current: currentEpic,
                        scoreDiff,
                        laneChanged
                    });
                } else {
                    unchanged.push(currentEpic);
                }
            }
        });

        // Find additions
        currentEpics.forEach(currentEpic => {
            if (!sessionMap.has(currentEpic.id)) {
                added.push(currentEpic);
            }
        });

        return { session, changes, added, removed, unchanged };
    }, [selectedSessionId, sessions, currentEpics]);

    // Sort sessions for dropdown
    const sortedSessions = useMemo(() => {
        return [...(sessions || [])].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );
    }, [sessions]);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50 backdrop-blur-sm"
                        onClick={onClose}
                    />
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            className="w-full max-w-4xl max-h-[85vh] bg-white dark:bg-[#1a1a24] rounded-2xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col"
                        >
                            {/* Header */}
                            <div className="px-6 py-4 border-b border-siemens-deep-blue/5 dark:border-[#3d3d4d] flex items-center justify-between bg-gradient-to-r from-siemens-petrol to-siemens-petrol-dark text-white">
                                <div className="flex items-center gap-3">
                                    <GitCompare className="w-6 h-6" />
                                    <div>
                                        <h2 className="text-xl font-bold">Session Compare</h2>
                                        <p className="text-xs text-white/70">Compare current state with historical snapshots</p>
                                    </div>
                                </div>
                                <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Controls */}
                            <div className="p-6 border-b border-siemens-deep-blue/5 dark:border-[#3d3d4d] bg-slate-50/50 dark:bg-[#13131a]">
                                <div className="flex items-end gap-4">
                                    <div className="flex-1">
                                        <label className="block text-xs font-bold text-siemens-text-soft uppercase mb-1">
                                            Select Session to Compare Against
                                        </label>
                                        <select
                                            value={selectedSessionId}
                                            onChange={(e) => setSelectedSessionId(e.target.value)}
                                            className="w-full p-2.5 rounded-lg border border-siemens-deep-blue/20 dark:border-[#3d3d4d] bg-white dark:bg-[#1a1a24] text-siemens-deep-blue dark:text-white focus:ring-2 focus:ring-siemens-petrol outline-none"
                                        >
                                            <option value="">Select a historical session...</option>
                                            {sortedSessions.map(s => (
                                                <option key={s.id} value={s.id}>
                                                    {s.label || s.name || new Date(s.createdAt).toLocaleString()} ({s.epics?.length || 0} items)
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    {comparison && (
                                        <div className="flex gap-4 px-4 py-2 bg-white dark:bg-[#1a1a24] rounded-lg border border-siemens-deep-blue/5 dark:border-[#3d3d4d]">
                                            <div className="text-center">
                                                <div className="text-lg font-black text-siemens-feedback-orange">{comparison.changes.length}</div>
                                                <div className="text-[10px] text-siemens-text-soft font-bold uppercase">Modified</div>
                                            </div>
                                            <div className="w-px bg-siemens-deep-blue/10 dark:bg-[#3d3d4d]" />
                                            <div className="text-center">
                                                <div className="text-lg font-black text-siemens-feedback-green">{comparison.added.length}</div>
                                                <div className="text-[10px] text-siemens-text-soft font-bold uppercase">Added</div>
                                            </div>
                                            <div className="w-px bg-siemens-deep-blue/10 dark:bg-[#3d3d4d]" />
                                            <div className="text-center">
                                                <div className="text-lg font-black text-siemens-feedback-blue">{comparison.removed.length}</div>
                                                <div className="text-[10px] text-siemens-text-soft font-bold uppercase">Removed</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6 space-y-8">
                                {!selectedSessionId ? (
                                    <div className="text-center py-20 opacity-50">
                                        <GitCompare className="w-16 h-16 mx-auto mb-4 text-siemens-petrol" />
                                        <p className="text-siemens-deep-blue dark:text-white font-medium">Select a session above to begin comparison</p>
                                    </div>
                                ) : (
                                    <>
                                        {/* Modified Items */}
                                        {comparison && comparison.changes.length > 0 && (
                                            <section>
                                                <h3 className="text-sm font-bold text-siemens-text-soft uppercase mb-3 flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-siemens-feedback-orange" />
                                                    Changed Items
                                                </h3>
                                                <div className="rounded-xl border border-siemens-deep-blue/10 dark:border-[#3d3d4d] overflow-hidden">
                                                    <table className="w-full text-sm">
                                                        <thead className="bg-slate-50 dark:bg-[#13131a] text-left">
                                                            <tr>
                                                                <th className="p-3 font-semibold text-siemens-deep-blue dark:text-white">Epic</th>
                                                                <th className="p-3 font-semibold text-siemens-deep-blue dark:text-white">Change</th>
                                                                <th className="p-3 font-semibold text-siemens-deep-blue dark:text-white">Score Diff</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-siemens-deep-blue/5 dark:divide-[#3d3d4d] bg-white dark:bg-[#1a1a24]">
                                                            {comparison.changes.map(item => (
                                                                <tr key={item.id}>
                                                                    <td className="p-3 font-medium text-siemens-deep-blue dark:text-white max-w-xs truncate">
                                                                        {item.title}
                                                                        <div className="text-xs text-siemens-text-soft font-normal">{item.current.projectId}</div>
                                                                    </td>
                                                                    <td className="p-3">
                                                                        <div className="flex items-center gap-2">
                                                                            {item.laneChanged && (
                                                                                <div className="flex items-center text-xs bg-slate-100 dark:bg-slate-800 rounded px-2 py-1">
                                                                                    <span className="opacity-50 line-through">{item.past.lane}</span>
                                                                                    <ArrowRight className="w-3 h-3 mx-1" />
                                                                                    <span className="font-bold">{item.current.lane}</span>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </td>
                                                                    <td className="p-3">
                                                                        <div className="flex items-center gap-1 font-bold">
                                                                            {item.scoreDiff > 0 ? (
                                                                                <ArrowUpRight className="w-4 h-4 text-green-500" />
                                                                            ) : item.scoreDiff < 0 ? (
                                                                                <ArrowDownRight className="w-4 h-4 text-red-500" />
                                                                            ) : (
                                                                                <Minus className="w-4 h-4 text-slate-400" />
                                                                            )}
                                                                            <span className={item.scoreDiff > 0 ? 'text-green-500' : item.scoreDiff < 0 ? 'text-red-500' : 'text-slate-400'}>
                                                                                {Math.abs(item.scoreDiff).toFixed(1)}
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </section>
                                        )}

                                        {/* Added Items */}
                                        {comparison && comparison.added.length > 0 && (
                                            <section>
                                                <h3 className="text-sm font-bold text-siemens-text-soft uppercase mb-3 flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-siemens-feedback-green" />
                                                    New Items (Added since snapshot)
                                                </h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {comparison.added.map(item => (
                                                        <div key={item.id} className="p-3 rounded-lg border border-siemens-deep-blue/10 dark:border-[#3d3d4d] bg-white dark:bg-[#1a1a24] flex justify-between items-center">
                                                            <div className="truncate pr-2">
                                                                <div className="font-medium text-siemens-deep-blue dark:text-white truncate">{item.title}</div>
                                                                <div className="text-xs text-siemens-text-soft">{item.lane} • WSJF: {item.wsjf?.toFixed(1)}</div>
                                                            </div>
                                                            <div className="px-2 py-1 rounded text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 uppercase">
                                                                New
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>
                                        )}

                                        {/* Removed Items */}
                                        {comparison && comparison.removed.length > 0 && (
                                            <section>
                                                <h3 className="text-sm font-bold text-siemens-text-soft uppercase mb-3 flex items-center gap-2">
                                                    <span className="w-2 h-2 rounded-full bg-siemens-feedback-blue" />
                                                    Removed / Completed
                                                </h3>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    {comparison.removed.map(item => (
                                                        <div key={item.id} className="p-3 rounded-lg border border-siemens-deep-blue/10 dark:border-[#3d3d4d] bg-slate-50 dark:bg-[#13131a] opacity-70 flex justify-between items-center">
                                                            <div className="truncate pr-2">
                                                                <div className="font-medium text-siemens-deep-blue dark:text-white truncate line-through">{item.title}</div>
                                                                <div className="text-xs text-siemens-text-soft">Was: {item.lane}</div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </section>
                                        )}

                                    </>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-siemens-deep-blue/5 dark:border-[#3d3d4d] bg-slate-50 dark:bg-[#13131a] flex justify-end">
                                <button onClick={onClose} className="px-6 py-2 bg-siemens-deep-blue dark:bg-slate-700 text-white font-semibold rounded-lg hover:opacity-90 transition-opacity">
                                    Close Comparison
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default CompareModal;
